import { Box, Button, Modal } from "@mui/material";
import { UseQueryResult } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import React, { useState } from "react"; // Add useState
import Loader from "../../components/Loader";
import { AppointmentsAPI } from "../../middleware";
import { AppointmentCard } from "./appointmentCard"; // Import the AppointmentCard component

// Define the props interface
interface CancelledAppointmentsTabProps {
  currentAppointmentsObject: UseQueryResult<AxiosResponse<any, any>, Error>;
  accessToken: string; // Pass accessToken
  patientId: string; // Pass patientId

}

const CancelledAppointmentsTab: React.FC<CancelledAppointmentsTabProps> = ({
  currentAppointmentsObject,
  accessToken,
  patientId
}) => {
  const [isLoading, setIsLoading] = useState(false); // Add loading state
  const [showSuccessModal, setShowSuccessModal] = useState(false); // Add success modal state
  const [showErrorModal, setShowErrorModal] = useState(false); // Add error modal state

  if (currentAppointmentsObject.isLoading) {
    return (
      <div className="h-[70vh] flex flex-row justify-center items-center w-full">
        <Loader />
      </div>
    );
  }

  if (currentAppointmentsObject.error) {
    return <div>Error: {currentAppointmentsObject.error.message}</div>;
  }

  if (!currentAppointmentsObject.data?.data) {
    return <p className="text-red-500 text-base">
      No cancelled appointments available
    </p>;
  }

  // Debug: Log all unique status values to see what's available
  const allStatuses = currentAppointmentsObject.data.data.appointments.map((app: any) => app.status);
  const uniqueStatuses = Array.from(new Set(allStatuses));
  console.log("All appointment statuses:", uniqueStatuses);

  // Filter cancelled appointments - check for various possible cancelled status values
  const cancelledAppointments = currentAppointmentsObject.data.data.appointments.filter(
    (visit: any) => {
      const status = visit.status?.toLowerCase();
      return status === "canceled" ||
        status === "cancelled" ||
        status === "cancel" ||
        visit.status === null; // Some cancelled appointments might have null status
    }
  );

  console.log("Cancelled appointments found:", cancelledAppointments.length);

  const handleCancelClick = async (appointmentId: string) => {
    setIsLoading(true); // Set loading to true
    try {
      // Call the cancel appointment API
      const response = await AppointmentsAPI.cancelAppointment(accessToken, "1", patientId, appointmentId);
      if (response.status === 200 || response.status === 201) {
        setShowSuccessModal(true); // Show success modal
      } else {
        setShowErrorModal(true); // Show error modal
      }
    } catch (error) {
      console.error("Error canceling appointment:", error);
      setShowErrorModal(true); // Show error modal
    } finally {
      setIsLoading(false); // Reset loading state
    }
  };

  return (
    <div className="md:fixed mt-3 flex justify-center">
      <div className="relative w-full h-[70vh] overflow-y-scroll">
        <div className="justify-start flex flex-col flex-wrap md:grid items-start grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 w-full relative max-h-[95%] mb-3 md:mb-0 overflow-y-scroll pb-2">
          {cancelledAppointments.length > 0 ? (
            cancelledAppointments.map((visit: any) => (
              <AppointmentCard
                key={visit.appointmentId}
                image={visit.image}
                specialistName={visit.specialistName}
                appointmentName={visit.appointmentName}
                appointmentCategoryTypeName={visit.appointmentCategoryTypeName}
                appointmentDate={visit.appointmentDate}
                appointStartTimeName={visit.appointStartTimeName}
                branchName={visit.branchName}
                status={visit.status}
                appointmentId={visit.appointmentId} // Pass the appointment ID
                accessToken={accessToken} // Pass accessToken
                healthFacilityId={visit.healthFacilityId} // Pass health facility ID
                patientId={visit.patientId} // Pass patient ID
                // You can add any additional props or functionality as needed
                onCancelClick={() => handleCancelClick(visit.appointmentId)} // Update to use handleCancelClick
              />
            ))
          ) : (
            <div className="flex w-full justify-center">
              <p className="text-red-500 text-base">No cancelled appointments available.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CancelledAppointmentsTab;
